import type { MarkdownDocument } from '@markbird/shared';
import { contentHash } from './folderSync/reconcile';
import { decide, needsPush, stateOf, type RemoteDoc, type SyncedState } from './syncDecide';

/**
 * The sync loop, without Firestore or Dexie in it.
 *
 * What makes this hold up for a library of thousands rather than dozens:
 *
 *  - it remembers, per document, the state both sides last agreed on, so a
 *    start-up pushes only what actually differs instead of the whole library,
 *    and a document that merely came back from the server is not mistaken for
 *    a local change (the echo that used to push everything twice);
 *  - local writes are coalesced and sent in batches, so a burst of autosaves
 *    is a few writes, not one per keystroke-batch;
 *  - a document deleted for good leaves a tombstone, so other devices delete it
 *    too. Deletions used to be invisible: the record was removed, and the
 *    listener ignores removals, so every other device kept its copy for ever.
 *
 * Everything either side would lose is preserved first: a losing version goes
 * into version history, and an edit made after a deletion brings the document
 * back rather than vanishing.
 */

/** How long a tombstone is kept before the store may drop it. */
export const TOMBSTONE_TTL_MS = 90 * 24 * 60 * 60 * 1000;
/** Quiet time before a burst of local writes is sent. */
const COALESCE_MS = 800;
/** Documents per batch, matching Firestore's limit for one write batch. */
export const BATCH_SIZE = 400;

export interface RemoteSync {
  /** Subscribe to remote changes for this owner. Returns an unsubscribe function. */
  watch(onDocs: (docs: RemoteDoc[]) => void, onError: () => void): () => void;
  /** Write documents and tombstones. The adapter batches; this may be called with many. */
  put(docs: RemoteDoc[]): Promise<void>;
}

export interface LocalSync {
  get(id: string): Promise<MarkdownDocument | undefined>;
  /** Every document, including trashed ones. */
  listAll(): Promise<MarkdownDocument[]>;
  /** Write a document that came from the remote side (no local-write echo). */
  apply(doc: MarkdownDocument): Promise<void>;
  /** Delete a document for good, following a tombstone. */
  purge(id: string): Promise<void>;
  /** Keep a version that lost a conflict, restorable from history. */
  preserve(doc: MarkdownDocument): Promise<void>;
  agreed(id: string): Promise<SyncedState | undefined>;
  setAgreed(state: SyncedState): Promise<void>;
  listAgreed(): Promise<SyncedState[]>;
}

export interface SyncEvents {
  /** Both sides had changed `doc`; the losing version is in history. */
  onConflict?: (doc: MarkdownDocument) => void;
  /** The document changed remotely and is open here. */
  onRemoteContent?: (doc: MarkdownDocument) => void;
  /** Documents still waiting to be sent or applied. */
  onPending?: (count: number) => void;
  onError?: () => void;
}

export interface SyncEngine {
  start(uid: string): Promise<void>;
  stop(): void;
  /** A document changed locally (or was deleted); send it when the burst settles. */
  queueLocal(id: string): void;
  /** Send everything that differs from the agreed state. Used at start-up. */
  pushPending(): Promise<number>;
  /** Wait for the queue to drain. Tests and "sync now". */
  flush(): Promise<void>;
}

function tombstone(id: string, ownerId: string, previous: MarkdownDocument | undefined): RemoteDoc {
  const now = Date.now();
  return {
    id,
    title: previous?.title ?? '',
    content: '',
    createdAt: previous?.createdAt ?? now,
    updatedAt: now,
    ownerId,
    deletedAt: now,
    purged: true,
    purgedAt: now,
    expiresAt: now + TOMBSTONE_TTL_MS,
  };
}

export function createSyncEngine(
  local: LocalSync,
  remote: RemoteSync,
  events: SyncEvents = {},
): SyncEngine {
  let uid: string | null = null;
  let unwatch: (() => void) | null = null;
  const queued = new Set<string>();
  /** Ids currently being written from a remote snapshot — their writes are not local changes. */
  const applying = new Set<string>();
  let timer: ReturnType<typeof setTimeout> | null = null;
  let work: Promise<void> = Promise.resolve();

  const report = () => events.onPending?.(queued.size);

  /** Run `job` after whatever is already running, so the order of changes is kept. */
  const serialize = (job: () => Promise<void>): Promise<void> => {
    work = work.then(job, job);
    return work;
  };

  async function applyRemote(docs: RemoteDoc[]): Promise<void> {
    const toPush: RemoteDoc[] = [];
    for (const remoteDoc of docs) {
      const id = remoteDoc.id;
      const current = await local.get(id);
      const agreed = await local.agreed(id);
      const action = decide(current, remoteDoc, agreed);

      if (action.kind === 'none') {
        // Nothing to do, but a first meeting still has to be written down, or
        // the same comparison would run again on every start.
        if (!agreed && current) await local.setAgreed(stateOf(current));
        continue;
      }
      if (action.kind === 'push') {
        const mine = current ?? undefined;
        if (!mine) {
          // Deleted here for good: the tombstone is what the others need. The
          // agreed state records the tombstone, so a copy that turns up later
          // can be told apart from the one we deleted by its timestamp.
          const stone = tombstone(id, uid ?? remoteDoc.ownerId ?? '', undefined);
          toPush.push(stone);
          await local.setAgreed({
            ...stateOf(stone),
            purged: true,
            purgedHash: agreed?.purgedHash ?? agreed?.contentHash,
          });
        } else {
          toPush.push(mine);
          await local.setAgreed(stateOf(mine));
        }
        continue;
      }
      if (action.kind === 'purge') {
        applying.add(id);
        await local.purge(id);
        applying.delete(id);
        // Remember what was deleted, so a copy that turns up later with other
        // text is recognised as writing this device never saw.
        await local.setAgreed({
          ...stateOf(remoteDoc),
          docId: id,
          purged: true,
          purgedHash:
            agreed?.purgedHash ?? (current ? contentHash(current.content) : agreed?.contentHash),
        });
        continue;
      }

      if (action.kind === 'conflict') await local.preserve(action.preserve);
      applying.add(id);
      await local.apply(action.doc);
      applying.delete(id);
      await local.setAgreed(stateOf(action.doc));
      if (action.kind === 'conflict') events.onConflict?.(action.doc);
      if (action.contentChanged) events.onRemoteContent?.(action.doc);
      // The merge kept something the remote copy does not have yet.
      if (needsPush(action.doc, remoteDoc)) toPush.push(action.doc);
    }
    if (toPush.length) await remote.put(toPush);
  }

  async function sendQueued(): Promise<void> {
    if (!uid || queued.size === 0) return;
    const ids = [...queued];
    queued.clear();
    report();
    const batch: RemoteDoc[] = [];
    for (const id of ids) {
      const doc = await local.get(id);
      if (!doc) {
        const agreed = await local.agreed(id);
        // Never synced and already gone: nothing to tell anyone about.
        if (!agreed || agreed.purged) continue;
        const stone = tombstone(id, uid, undefined);
        batch.push(stone);
        await local.setAgreed({ ...stateOf(stone), purged: true, purgedHash: agreed.contentHash });
        continue;
      }
      if (doc.ownerId !== uid) continue;
      batch.push(doc);
      await local.setAgreed(stateOf(doc));
    }
    for (let i = 0; i < batch.length; i += BATCH_SIZE) {
      await remote.put(batch.slice(i, i + BATCH_SIZE));
    }
  }

  return {
    async start(nextUid) {
      uid = nextUid;
      unwatch?.();
      // The first delivery is applied before anything is pushed: otherwise a
      // local copy would be sent up before this device has seen that the
      // document was deleted, or changed, elsewhere.
      let firstBatch: (() => void) | null = null;
      const seen = new Promise<void>((resolve) => {
        firstBatch = resolve;
      });
      unwatch = remote.watch(
        (docs) =>
          void serialize(async () => {
            await applyRemote(docs);
            firstBatch?.();
            firstBatch = null;
          }),
        () => {
          events.onError?.();
          firstBatch?.();
          firstBatch = null;
        },
      );
      // Offline, nothing may arrive at all; the push still has to happen.
      await Promise.race([seen, new Promise((r) => setTimeout(r, 5_000))]);
      await this.pushPending();
    },

    stop() {
      unwatch?.();
      unwatch = null;
      uid = null;
      queued.clear();
      if (timer) clearTimeout(timer);
      timer = null;
      report();
    },

    queueLocal(id) {
      if (!uid || applying.has(id)) return;
      queued.add(id);
      report();
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        timer = null;
        void serialize(sendQueued);
      }, COALESCE_MS);
    },

    async pushPending() {
      if (!uid) return 0;
      const agreedStates = new Map((await local.listAgreed()).map((s) => [s.docId, s]));
      const docs = await local.listAll();
      const pending: RemoteDoc[] = [];
      for (const doc of docs) {
        if (doc.ownerId !== uid) continue;
        const agreed = agreedStates.get(doc.id);
        const state = stateOf(doc);
        if (
          agreed &&
          !agreed.purged &&
          agreed.contentHash === state.contentHash &&
          agreed.updatedAt === state.updatedAt &&
          agreed.metaUpdatedAt === state.metaUpdatedAt
        ) {
          continue; // already agreed: nothing to send
        }
        pending.push(doc);
      }
      // Documents this device deleted for good while it was offline.
      const known = new Set(docs.map((d) => d.id));
      const purgedHashes = new Map<string, string>();
      for (const [id, agreed] of agreedStates) {
        if (known.has(id) || agreed.purged) continue;
        pending.push(tombstone(id, uid, undefined));
        purgedHashes.set(id, agreed.contentHash);
      }
      for (let i = 0; i < pending.length; i += BATCH_SIZE) {
        await remote.put(pending.slice(i, i + BATCH_SIZE));
      }
      for (const doc of pending) {
        await local.setAgreed(
          doc.purged
            ? { ...stateOf(doc), purged: true, purgedHash: purgedHashes.get(doc.id) }
            : stateOf(doc),
        );
      }
      return pending.length;
    },

    async flush() {
      if (timer) {
        clearTimeout(timer);
        timer = null;
        await serialize(sendQueued);
      }
      await work;
    },
  };
}
